// Edit / Delete on anything typed as a note — account activity, project
// notes, PO notes, follow-ups (Stephanie 2026-10-30: "anything that a rep
// enters should be editable and allow us to delete"). One component so every
// note behaves the same: small Edit · Delete links, an inline editor, and a
// "Delete this …? Yes / No" check before anything is removed.
import { useState, type ReactNode } from "react";
import { apiErrorMessage } from "@/lib/api";

export interface NoteEditField {
  key: string;
  label: string;
  value: string;
  /** "text" (default), "date" or "textarea". */
  kind?: "text" | "date" | "textarea";
  required?: boolean;
}

export function EditDeleteNote({
  noun = "note",
  fields,
  onSave,
  onDelete,
  children,
}: {
  noun?: string;
  fields: NoteEditField[];
  onSave: (values: Record<string, string>) => Promise<unknown>;
  onDelete: () => Promise<unknown>;
  children: ReactNode;
}) {
  const [mode, setMode] = useState<"view" | "edit" | "confirm">("view");
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const start = (): void => {
    setValues(Object.fromEntries(fields.map((f) => [f.key, f.value])));
    setErr(null);
    setMode("edit");
  };
  const run = async (fn: () => Promise<unknown>, fallback: string): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      setMode("view");
    } catch (e) {
      setErr(apiErrorMessage(e, fallback));
    } finally {
      setBusy(false);
    }
  };
  const link = "text-xs font-medium text-muted hover:text-ink disabled:opacity-50";

  if (mode === "edit") {
    const missing = fields.find((f) => f.required !== false && !values[f.key]?.trim());
    return (
      <form
        className="space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (missing) {
            setErr(`${missing.label} can't be blank.`);
            return;
          }
          void run(() => onSave(values), `We couldn't save the ${noun}. Nothing was changed — try again.`);
        }}
      >
        {fields.map((f) => (
          <label key={f.key} className="block text-xs font-medium text-muted">
            {fields.length > 1 ? f.label : <span className="sr-only">{f.label}</span>}
            {f.kind === "textarea" || (!f.kind && fields.length === 1) ? (
              <textarea
                value={values[f.key] ?? ""}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                rows={3}
                autoFocus
                className="mt-1 block w-full rounded-md border border-rule bg-white px-3 py-2 text-sm text-ink"
              />
            ) : (
              <input
                type={f.kind === "date" ? "date" : "text"}
                value={values[f.key] ?? ""}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                className="mt-1 block h-9 w-full rounded-md border border-rule bg-white px-3 text-sm text-ink"
              />
            )}
          </label>
        ))}
        {err && <p className="text-xs text-rust-600">{err}</p>}
        <div className="flex gap-2">
          <button type="submit" disabled={busy} className="rounded-md bg-ink px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">
            {busy ? "Saving…" : "Save"}
          </button>
          <button type="button" disabled={busy} onClick={() => setMode("view")} className="px-2 text-xs text-ink">
            Cancel
          </button>
        </div>
      </form>
    );
  }

  return (
    <div>
      {children}
      <div className="mt-1 flex flex-wrap items-center gap-3">
        {mode === "confirm" ? (
          <>
            <span className="text-xs text-ink">Delete this {noun}?</span>
            <button
              type="button"
              disabled={busy}
              onClick={() => void run(onDelete, `We couldn't delete the ${noun}. Nothing was changed — try again.`)}
              className="text-xs font-semibold text-rust-600 hover:underline disabled:opacity-50"
            >
              {busy ? "Deleting…" : "Yes, delete"}
            </button>
            <button type="button" disabled={busy} onClick={() => setMode("view")} className={link}>
              No
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={start} className={link}>
              Edit
            </button>
            <button type="button" onClick={() => { setErr(null); setMode("confirm"); }} className={link}>
              Delete
            </button>
          </>
        )}
      </div>
      {err && <p className="mt-1 text-xs text-rust-600">{err}</p>}
    </div>
  );
}
